// Import enum types
import type {
  CheckinVersion,
  ClothingSize,
  ClothingType,
  CollaboratorRole,
  CollaboratorStatus,
  ContactRole,
  ContractType,
  CoproApprovalStatus,
  DesignStatus,
  EquipmentType,
  ImplementationTypology,
  LaunchClearanceStatus,
  LeaseContractType,
  NoGradeReason,
  PlanningOptimizationStatus,
  ProjectTypology,
  ResolutionStatus,
  SecurisationType,
  UnavailabityReason,
  WeekDay,
  WorkShiftClockAction,
} from './enums/index'

// Base type for data with timestamps
export interface TimestampsAndID {
  id: number

  createdAt: string
  updatedAt: string
}

// User data
export interface User extends TimestampsAndID {
  email: string
  firstName: string
  lastName: string
  isReliable: boolean
  isBanned: boolean
  unBanAmount: number | null
  worksConcilId: number | null
  studentDocumentRectoUrl: string | null
  studentDocumentVersoUrl: string | null
  isValidatedStudent: boolean | null // null means not validated yet
  storeId: number | null
  socketToken: string | null
  socketAiUrl: string | null
  socketStreamUrl: string | null
  socketUrl: string | null
  streamUrl: string | null
}

// Admin data
export interface Admin extends TimestampsAndID {
  email: string
  firstName: string
  lastName: string
  adminRights: number
  isDeleted: boolean
  socketToken: string | null
  socketAiUrl: string | null
  socketStreamUrl: string | null
  socketUrl: string | null
  streamUrl: string | null
  stores?: Store[] // Associated stores for this admin
}

// Type guards to differentiate between User and Admin
export const isUser = (user: User | Admin): user is User => !('adminRights' in user)

export const isAdmin = (user: User | Admin): user is Admin => 'adminRights' in user

// User action log
export interface UserActionLog {
  id: number
  createdAt: string
  userId: number
  adminId: number | null
  action: string
  detail: string
  userSnapshot: Record<string, unknown>
  user?: User | null
  admin?: Admin | null
}

// Store action log
export interface StoreActionLog {
  id: number
  createdAt: string
  storeId: number
  adminId: number | null
  action: string
  detail: string
  storeSnapshot: Record<string, unknown>
  store?: Store | null
  admin?: Admin | null
}

// Product action log
export interface ProductActionLog {
  id: number
  createdAt: string
  productId: number
  adminId: number | null
  action: string
  detail: string
  productSnapshot: Record<string, unknown>
  product?: Product | null
  admin?: Admin | null
}

export interface AttachmentFile {
  name: string
  extname: string
  size: number
  mimeType: string
  url?: string // Depends on preComputeUrl from the backend
}

// Category data
export interface Category extends TimestampsAndID {
  name: string
  image?: AttachmentFile
  categoryId: number | null
  isSubCategory: boolean
  description?: string
  rank: number
}

export interface InternalCategory extends TimestampsAndID {
  name: string
  trackDlc: boolean
  products: number[]
}

// Reappro Category data
export interface ReapproCategory extends TimestampsAndID {
  name: string
  daysBefore: number
  coverageRate: number
  dispatchDaysBefore: number | null
  dispatchCoverageRate: number | null
  computeHour: string // "HH:mm"
  sendHour: string
  logHour: string
  dispatchComputeHour: string | null
  dispatchSendHour: string | null
  products: number[]
  isDispatch: boolean
}

export type ReapproAlgorithm = 'VMAX' | 'VMJ' | 'STOCK_PROJETE'
export type ProductReapproAlgorithm = ReapproAlgorithm | 'STOCK_MINIMUM'
export type ReaproPeriode = '3J' | '5J' | '7J'

export const REAPPRO_ALGORITHM_IDS = {
  VMAX: 1,
  VMJ: 2,
  STOCK_PROJETE: 3,
} as const

export type ReapproAlgorithmId = (typeof REAPPRO_ALGORITHM_IDS)[ReapproAlgorithm]

type Invert<T extends Record<PropertyKey, PropertyKey>> = {
  [K in keyof T as T[K]]: K
}

export const REAPPRO_ALGORITHM_BY_ID = Object.fromEntries(
  Object.entries(REAPPRO_ALGORITHM_IDS).map(([k, v]) => [v, k]),
) as Invert<typeof REAPPRO_ALGORITHM_IDS>

export interface ReapproAlgorithmParam extends TimestampsAndID {
  algorithm: ReapproAlgorithm
  daysBetweenDeliveries: number | null
}

export interface ReapproAlgorithmParamUpdate {
  id: ReapproAlgorithmId
  daysBetweenDeliveries: number | null
}

export interface StoreReapproCategoryAlgorithm extends TimestampsAndID {
  storeId: number
  reapproCategoryId: number
  algorithm: ReapproAlgorithm
  periode: ReaproPeriode
  coefSecu: number
  coefWeekend: number
  coverageRate: number | null
  dispatchCoverageRate: number | null
}

export interface ReapproCategoryAlgorithmUpdate {
  storeId: number
  reapproCategoryId: number
  algorithm: ReapproAlgorithm
  periode: ReaproPeriode
  coefSecu: number
  coefWeekend: number
  coverageRate?: number | null
  dispatchCoverageRate?: number | null
}

export interface StoreProductReapproParam extends TimestampsAndID {
  storeId: number
  productId: number
  algorithm: ProductReapproAlgorithm | null
  periode: string | null
  coverageRate: number | null
  coefSecu: number | null
  coefWeekend: number | null
  stockMinimum: number | null
  isDisabled: boolean
}

export interface StoreProductReapproParamUpsert {
  storeId: number
  productId: number
  algorithm: ProductReapproAlgorithm | null
  periode: string | null
  coverageRate: number | null
  coefSecu: number | null
  coefWeekend: number | null
  stockMinimum: number | null
  isDisabled: boolean
}

export interface ReapproProductPreview {
  productId: number
  reference: string
  effectiveAlgorithm: ProductReapproAlgorithm
  effectivePeriode: ReaproPeriode
  effectiveCoverageRate: number
  effectiveCoefSecu: number
  effectiveCoefWeekend: number
  resolvedStockMinimum: number | null
  /** True when algorithm was forced to STOCK_MINIMUM because productionCoveringRate is absent */
  forcedStockMinimum: boolean
  besoin: number
  stockCible: number | null
  stockActuel: number
  stockDLCCourte: number
  stockArrivant: number
  stockProjete: number
  vma: number
  vmj: number
  prevision: number
  currentStockMin: number
  daysBefore: number
  stopOrdering: boolean
  poidsJourJ: number
  poidsJourJ1: number
  isDisabled: boolean
}

// Brand data
export interface Brand extends TimestampsAndID {
  name: string
  image?: AttachmentFile // Optional image
}

export const MENU_TYPES = ['PLAT', 'DESSERT', 'BOISSON'] as const

export type MenuItemType = (typeof MENU_TYPES)[number]

export const MENU_TYPES_OPTIONS = [
  { label: 'Aucun' as const, value: null },
  ...MENU_TYPES.map((type) => ({
    label: type,
    value: type,
  })),
]

export const NEGOCE_TYPES = ['NEGOCE INTERNE', 'NEGOCE EXTERNE', 'SUSHI'] as const

export type NegoceType = (typeof NEGOCE_TYPES)[number]

const NEGOCE_OPTIONS_OBJ: { [K in NegoceType]: string } = {
  'NEGOCE EXTERNE': 'Négoce externe',
  'NEGOCE INTERNE': 'Négoce interne',
  SUSHI: 'Sushi',
}

export const NEGOCE_TYPES_OPTIONS: { value: NegoceType; label: string }[] = Object.entries(
  NEGOCE_OPTIONS_OBJ,
).map(([value, label]) => ({ value: value as NegoceType, label }))

// Product data returned by the Index query
export interface Product extends TimestampsAndID {
  // Product label
  reference: string
  dun14: string | null
  label: string

  // Product weight and price
  weight: number // grams
  price: number // euros
  weightPrice: number // centimes per gram
  sellPrice: number // euros
  sellWeightPrice: number // centimes per gram

  // Product description
  description: string
  nutriscore: 'A' | 'B' | 'C' | 'D' | 'E' | null
  conservation: string
  preparationMode: string | null
  allergens: string | null
  ingredients: string
  antiGaspi: number // Number before DLC to warn the user
  portions: string | null

  // Metadata
  isNew: boolean
  isActive: boolean
  colisage: number
  colisWeight: number // grams

  // Additional data from other tables
  brandId: number
  brand: Brand

  menuType: MenuItemType | null
  menuExtraPrice: number | null
  copilotCode: string | null
  negoceType: NegoceType

  categories: Category[]
  internalCategoryId: number
  internalCategory: InternalCategory
  reapproCategory: ReapproCategory
  reapproCategoryId: number

  nutritionalValueId: number
  nutritionalValues?: NutritionalValues

  optimizedImageUrls: string[]
  optimizedPreviewImageUrl: string | null

  // Attachments
  images?: ProductImage[]
  preparationVideo?: AttachmentFile // This is a video
  previewImage?: AttachmentFile // This is an image 9x16 vignette

  agPromo: number // ingeter : 70 for 70%
  agPrice: number
  taxRate: number // TVA rate for this product (5.5, 10, 20)
  dlcDays: number
  franco: number
  coldStartStockMin: number
  stopOrdering: boolean
  productionCoveringRate: number | null

  establishment?: Establishment
}

// Product data returned by the Show query (everything is loaded)
export interface LoadedProduct extends Product {
  // Attachments
  images: ProductImage[]
  nutritionalValues: NutritionalValues
}

export interface StoreProductMinimumStock extends TimestampsAndID {
  storeReference: string
  productReference: string
  weekEndMinimumStock: number | null
  mondayMinimumStock: number | null
  tuesdayMinimumStock: number | null
  wednesdayMinimumStock: number | null
  thursdayMinimumStock: number | null
  fridayMinimumStock: number | null
  label?: string // From the join with the store
}

export interface CampaignInfos {
  label: string
  reductionType: string
  reductionValue: number
}

export interface PromoProduct {
  product: Product
  campaignInfos?: CampaignInfos
  isAntiGaspi?: boolean
}

export interface NutritionalValues extends TimestampsAndID {
  energyValue: string // kcal
  fats: string // micrograms (high precision with integer)
  proteins: string // micrograms (high precision with integer)
  carbohydrates: string // micrograms (high precision with integer)
  sugars: string // micrograms (high precision with integer)
  polyols: string // micrograms (high precision with integer)
  fibers: string // micrograms (high precision with integer)
  sodium: string // micrograms (high precision with integer)
  salt: string // micrograms (high precision with integer)
  saturatedFats: string // micrograms (high precision with integer)
}

export interface ProductImage extends TimestampsAndID {
  productId: number
  image: AttachmentFile
}

export const CAMPAIGN_TARGETS = ['Produit', 'Utilisateur', 'Menu', 'Panier'] as const
export type CampaignTarget = (typeof CAMPAIGN_TARGETS)[number]
export const CAMPAIGN_TYPES = ['Taux', 'BRI', 'ONE_FOR_ONE'] as const
export type CampaignType = (typeof CAMPAIGN_TYPES)[number]

// Base campaign, with no target relation preloaded (used for the index query, that returns everything)
export interface PromoCampaignBase extends TimestampsAndID {
  type: CampaignTarget
  name: string
  isActive: boolean

  isMain: boolean
  alreadyUsed: boolean

  reductionType: CampaignType
  reductionValue: number

  startDate: string
  endDate: string
  isPublic: boolean
  publicCode: string | null
  isUsingUniqueCodes: boolean
}

export interface PromoUniqueUseCode extends TimestampsAndID {
  promoCampaignId: number
  code: string
  isUsed: boolean
}

export interface UserPromoCampaign extends PromoCampaignBase {
  type: 'Utilisateur'
  userTargets: User[]
  storeTargets: Store[]
}

export interface ProductPromoCampaign extends PromoCampaignBase {
  type: 'Produit'
  productTargets: Product[]
  storeTargets: Store[]
}

export interface MenuPromoCampaign extends PromoCampaignBase {
  type: 'Menu'
  menuTargets: string[]
  storeTargets: Store[]
}

export interface UserPromoCode extends TimestampsAndID {
  promoCampaign: PromoCampaignBase
  numberOfUses: number
  isMain: boolean
  alreadyUsed: boolean
}

// Campaign with loaded target (used in the show query, that returns only a specific campaign)
export type PromoCampaign = UserPromoCampaign | ProductPromoCampaign | MenuPromoCampaign

/** Helper : get a list of target IDs */
export const getTargetIds = (campaign: PromoCampaign): (number | string)[] => {
  if (campaign.type === 'Produit') {
    return campaign.productTargets.map((product) => product.id)
  }
  if (campaign.type === 'Utilisateur') {
    return campaign.userTargets.map((user) => user.id)
  }
  if (campaign.type === 'Menu') {
    return campaign.menuTargets
  }
  return []
}

/** Helper : get a list of store references */
export const getStoreTargetRefs = (campaign: PromoCampaign): string[] => {
  if (campaign.storeTargets) {
    return campaign.storeTargets.map((store) => store.checkpointStoreRef)
  }
  return []
}

// Store data
export interface Address extends TimestampsAndID {
  address: string
  addressAdditionnal?: string
  postalCode: string
  city: string
  country: Country
}

export const STORE_TYPES = [
  'BOUTIQUE',
  'SHIPPING',
  'WAREHOUSE',
  'POUBELLE',
  'INTERNAL_STOCK',
  'REMOTE',
] as const

export type StoreType = (typeof STORE_TYPES)[number]

export const CAMERA_TYPES = ['JVC', 'UNIFI'] as const
export type CameraType = (typeof CAMERA_TYPES)[number]

export const COUNTRIES = ['FRANCE'] as const

export type Country = (typeof COUNTRIES)[number]

export interface Store extends TimestampsAndID {
  checkpointStoreRef: string
  label: string
  addressId: number
  address: Address
  storeType: StoreType
  cameraType: CameraType | null
  isOnline: boolean
  lunaStoreRef: string | null
  bearcodName: string | null
  legalEntityId: number
  siret: string | null
  openingDate: string
  quantityFactor: number
  growthFactor: number
}

export interface Kiosk extends TimestampsAndID {
  label: string
  cpiId: string
  store: Store
  supportMode: boolean
  cartId: number
  transaction: string
  cart?: Cart
}

export const PRE_AUTHORIZATION_STATUSES = ['NONE', 'PENDING', 'SUCCESS', 'FAILED'] as const
export type PreAuthorizationStatus = (typeof PRE_AUTHORIZATION_STATUSES)[number]

export interface PreAuthorization extends TimestampsAndID {
  cpiId: string
  kioskId: number
  storeId: number
  preAuthorizationAmount: number
  cpiCreatedAt: string
  status: PreAuthorizationStatus
  chargeAmount: number | null
  chargeError: string | null
  receipt: string | null
  kiosk?: Kiosk
  store?: Store
}

export interface PendingPreAuthorizationRequest {
  requestId: number
  kioskId: number
  storeId: number
  kioskLabel: string
  storeLabel: string
  createdAt: string
}

// CPI OPENING EVENTS
export * from './schemas/openingEvents'
export * from './types/openingEvents.types'

export const UNIQUE_ARTICLE_SOLD_STATUSES = [
  'NORMAL',
  'ANTI_GASPI',
  'MENU',
  'PROMO',
  'PROMO_MENU',
  'STUDENT_MENU',
  'FREE',
  'WORKS_CONCIL',
  'STUDENT',
  'OTHER',
  'BRI',
] as const

export type UniqueArticleSoldStatus = (typeof UNIQUE_ARTICLE_SOLD_STATUSES)[number]

export const CART_ITEM_TYPES = ['ARTICLE', 'MENU'] as const

export type CartItemType = (typeof CART_ITEM_TYPES)[number]

export const UNIQUE_ARTICLE_PAID_WAY = ['APP', 'CPI'] as const

export type UniqueArticlePaidWay = (typeof UNIQUE_ARTICLE_PAID_WAY)[number]

export const UNIQUE_ARTICLE_SHOPPING_WAY = ['IN_STORE', 'CLICK_AND_COLLECT'] as const

export type UniqueArticleShoppingWay = (typeof UNIQUE_ARTICLE_SHOPPING_WAY)[number]
export interface UniqueArticle extends TimestampsAndID {
  checkpointArticleRef: string
  productReference: string
  storeReference: string
  weight: number // Grams
  dlc: string // ISO date string

  displayedDlc: string // Computed field, formatted like 27/02/2024
  isAntiGaspi: boolean // Computed field
  isExpired: boolean // Computed field
  isConfirmed: boolean // Computed field

  product: Product // Preloaded by default
  store: Store // not preloaded by default

  state: UniqueArticleState

  soldPrice: number | null
  soldStatus: UniqueArticleSoldStatus | null
  paidWay: UniqueArticlePaidWay | null
  shoppingWay: UniqueArticleShoppingWay | null
  buyingPrice: number | null

  warehouseAt: string | null // ISO date string
  shippingAt: string | null // ISO date string
  boutiqueAt: string | null // ISO date string
  lostAt: string | null // ISO date string
}

export interface WorksConcilPreview extends TimestampsAndID {
  label: string
  isActive: boolean
  maxMonthAmountPerEmployee: number
  gozokiPercentage: number
  worksConcilPercentage: number
  numberOfEmployees: number
  totalUsedAmount: number
  numberOfActivesEmployees: number
  startDate: string // ISO date string
  endDate: string // ISO date string
}

export interface WorksConcil extends TimestampsAndID {
  label: string
  isActive: boolean
  isOnDate: boolean

  companyLabel: string
  companyCity: string
  companyAddress: string
  companySiren: string

  maxMonthAmountPerEmployee: number
  gozokiPercentage: number
  worksConcilPercentage: number

  startDate: string // ISO date string
  endDate: string // ISO date string

  emails: string[]
  employees: User[]
}

export const CART_STATUSES = ['pending', 'validated', 'cancelled', 'stolen'] as const

export type CartStatus = (typeof CART_STATUSES)[number]

export interface Cart extends TimestampsAndID {
  userId: number | null
  status: CartStatus
  user?: User // Not preloaded by default !
  report: CartReportWithTaxes
  articles: UniqueArticle[] // Preloaded by default
  buyingPrice: number | null
  amountTotal?: number
  store: Store // Preloaded by default
}

export interface CartsWithReportUrl extends Cart {
  reportUrl: string
}

export interface PaymentDefaultUser extends User {
  carts: CartsWithReportUrl[]
}

/**
 * Lightweight cart summary used in the customer info modal opened from the
 * "Paniers" page in the BO. Only fields actually displayed in the history
 * table are included; full Cart payload is not needed.
 */
export interface CartHistoryEntry {
  id: number
  storeId: number
  validatedAt: string | null
  amountTotal: number
  itemCount: number
}

/**
 * Discriminated payload returned by `GET /carts/:id/customer-info`.
 * - `anonymous`: cart paid with a bare credit card (no user account).
 * - `user`: cart linked to a registered user; includes profile + last 20
 *   validated carts of that user.
 */
export type CartCustomerInfo =
  | { kind: 'anonymous' }
  | { kind: 'user'; user: User; history: CartHistoryEntry[] }

export interface CartReport {
  items: FinalCartUnit[]
  subTotal: number
  otherPromotions: CartPromotion[]
  total: number
}

export interface CartPromotion {
  type: PromoType
  label: string
  amount: number
}

export interface CartTax {
  rate: number
  amount: number
}

interface ItemBase {
  label: string
  unitPrice: number
  quantity: number
  amount: number
}

export interface ProductItem extends ItemBase {
  type: 'ARTICLE'
  productReference: string
  article: UniqueArticle
}

const PROMO_TYPES = ['ANTI_GASPI', 'ONE_FOR_ONE', 'WORKS_CONCIL', 'BRI', 'STUDENT'] as const

export type PromoType = (typeof PROMO_TYPES)[number]

export interface CartReportWithTaxes extends CartReport {
  totalExTax: number
  taxes: CartTax[]
}
export interface CartUnit {
  item: ProductItem
  promotions: CartPromotion[]
}

export interface FinalCartUnit {
  label: string
  unitPrice: number
  quantity: number
  amount: number
  type: 'ARTICLE' | 'MENU'
  content: { label: string }[]
  imageUrl: string | null
  promotions: CartPromotion[]
  taxRate: number
}
// Stock data
export interface StockMetadata {
  articleCount: number
  antiGaspiArticleCount: number
  expiredArticleCount: number
  totalStockValue: number
  totalAntiGaspiStockValue: number
  totalExpiredStockValue: number
}

export interface StockEntry {
  id: number
  imageUrl: string | null
  reference: string
  label: string
  colisage: number
  weight: number | null

  buyingPrice: number

  stock: number
  agStock: number
  expiredStock: number
  expiredOrWillExpireStock: number
  vma: number
  vjm: number
  vma3j: number
  vma5j: number
  vma7j: number
  vjm3j: number
  vjm5j: number
  vjm7j: number

  dailyMinStocksMap: { [key: string]: number }
  dun14: string

  totalValue: number
  totalValueAntiGaspi: number
  totalValueExpired: number
  antiGaspiValue: number

  currentReaproNeeds: number
  warehouseIn: string

  establishmentId: number
  sellerId: number
  internalCategory: InternalCategory
}

export interface StockData {
  metadata: StockMetadata
  rows: StockEntry[]
}

export interface QrCode {
  token: string
  image?: string
}

export interface PayzenPaymentToken {
  paymentMethodToken: string
  paymentMethodType: 'CARD' | 'SDD'
  creationDate: string
  cancelationDate: string | null
  status: 'ACTIVE' | 'CANCELLED' | 'EXPIRED' | 'ALL'
  tokenDetails: {
    expiryMonth: number
    expiryYear: number
    effectiveBrand: string
    pan: string
  }
}

export interface PaymentResponse {
  _type: string
  customer: {
    email: string
    reference: string | null
  }
  orderDetails: {
    _type: string
    orderId: string
    orderamountTotal: number
  }
  serverDate: string
  orderStatus: string
  transactions: {
    paymentMethodToken: string
    paymentMethodType: string
    shopId: string
    status: string
    uuid: string
  }[]
}
export const UNIQUE_ARTICLE_STATES = [
  'SOLD',
  'IN_STOCK',
  'FAULTY', // Not used anymore
  'STOLEN',
  'DESTROYED',
  'IN_CART',
  'DELETED', // use as a soft delete to keep the history
  'DEFECTIVE_PACKAGING',
  'INVENTORY_DELETED',
  'LOST_IN_TRANSPORT',
] as const

export type UniqueArticleState = (typeof UNIQUE_ARTICLE_STATES)[number]

export const DEFAULT_UNIQUE_ARTICLE_STATE: UniqueArticleState = 'IN_STOCK'
export interface DailyDashboardKpi {
  id: number
  storeId: number
  date: string
  ca: number
  caHt: number
  caAntiGaspi: number
  caAntiGaspiHt: number
  buyingPrice: number
  productsSoldCount: number
  antiGaspiProductsSold: number
  cartsCount: number
  productsSoldMap: { [key: string]: { ref: string; count: number } }
  productsSoldMapByCa: { [key: string]: { ref: string; ca: number } }
  categoriesSoldMap: { [key: number]: { id: number; count: number } }
  categoriesSoldMapByCa: { [key: number]: { id: number; ca: number } }
  appUsersCount: number
  appNewUsersCount: number
  appDeletedUsersCount: number
  appActivatedUsersCount: number
  appCartsCount: number
  appUsersList: number[]
  studentAmount: number
  studentCartsCount: number
  studentCartsCa: number
  studentUsersCount: number
  wcCa: number
  wcCartsCount: number
  wcGozokiAmount: number
  wcCompanyAmount: number
}

export interface DailyStockKpi {
  id: number
  storeId: number
  date: string
  articlesSoldMap: { [key: number]: { ref: string; count: number } }
  articlesStolenMap: { [key: number]: { ref: string; count: number } }
  articlesFaultyMap: { [key: number]: { ref: string; count: number } }
  articlesDestroyedMap: { [key: number]: { ref: string; count: number } }
  articlesExpiredMap: { [key: number]: { ref: string; count: number } }
  articlesDefectiveMap: { [key: number]: { ref: string; count: number } }
  articlesSoldCount: number
  articlesFaultyCount: number
  articlesDestroyedCount: number
  articlesStolenCount: number
  articlesExpiredCount: number
  articlesDefectiveCount: number
}

export interface AccountingKpis {
  date: string
  main: {
    internalNegoceHt: number
    internalNegoceTva: number
    externalNegoceHt: number
    externalNegoceTva: number
    sushisTtc: number
    amountCb: number
    amountConecs: number
    unPaidAmount: number
    promoAmountHt: number
    promoAmountTva: number
  }
}

export interface HoursData {
  time: number
  count: number
  amount: number
}

export interface TimeSpentData {
  time: number
  averageTimeInShop: number
  averageTimeInDesk: number
}

export interface Lot {
  lot: string
  ean: string
  nb: number
  label: string
  dlc: string
  tracking: string
  blocked: boolean
  comment: string
  blockedAt: string | null
  blockedBy: string | null
  unBlockedAt: string | null
  articles: string[]
}

export interface GlobalParams {
  studentPromoPercentage: number
  menuPlatBoissonName: string
  menuPlatBoissonPrice: number
  menuPlatBoissonDessertName: string
  menuPlatBoissonDessertPrice: number
}

export interface StoreStock {
  storeReference: string
  stock: number
  label: string
}

export interface NotificationPayload {
  path: string | null | undefined
}

export interface Notification {
  id: number
  title: string
  subTitle: string
  text: string
  onlyUsers: boolean
  shopIds: number[]
  recipients: number
  interactions: number
  createdAt: Date
  updatedAt: Date
  data: NotificationPayload | null
}

export interface AnomalyAPIResponse {
  id: string
  anomalyTypeId: string
  link: string | null
  timestamp: string
  // temporaryLink: string;
  videoId: string
  startTime: string
}

export interface CameraStore {
  id: string
  name: string
}

export interface AiArticle {
  productLabel: string
  productImageUrl: string | null
  productQuantity: number
}

export interface VideoIdsResponse {
  video_id: number[]
}

export interface Anomaly {
  name: string
  link: string | null
  temporaryLink: string
  id: string
  anomalyTypeId: string
  timestamp: string
  color: string
}

export const KIOSK_TYPES = ['CHECK_IN', 'CHECK_OUT'] as const
export type KioskType = (typeof KIOSK_TYPES)[number]

export const INVENTORY_TYPES = ['CLASSIC', 'TRANSPORT'] as const
export type InventoryType = (typeof INVENTORY_TYPES)[number]

export const INVENTORY_STATUS = ['pending', 'validating', 'validated', 'canceled'] as const
export type InventoryStatus = (typeof INVENTORY_STATUS)[number]

export const INVENTORY_LOG_ACTIONS = [
  'create',
  'add-articles',
  'finish',
  'unfinish',
  'validate',
  'revalidate',
  'cancel',
] as const
export type InventoryLogAction = (typeof INVENTORY_LOG_ACTIONS)[number]

export const CART_ARTICLE_LOG_EVENTS = [
  'added-to-cart',
  'not-found',
  'expired',
  'blocked',
  'bad-state',
  'created-from-epc-decode',
  'created-from-sold-duplicate',
  'sanitized-inventory-deleted',
  'sanitized-lost-in-transport',
  'store-mismatch',
  'sold',
  'stolen',
  'cancelled',
  'epc-decode-failed',
  'epc-bad-format',
  'ean13-mismatch',
] as const
export type CartArticleLogEvent = (typeof CART_ARTICLE_LOG_EVENTS)[number]

export interface UnscannableProduct {
  productReference: string
  count: number
}

export interface InventorySummary extends TimestampsAndID {
  label: string
  comment: string
  storeReference: number
  unScannableProducts?: { unScannableProducts: UnscannableProduct[] }
  status: InventoryStatus
  inventoryType: InventoryType
  isFinished: boolean
}

export interface InventoryDetail extends InventorySummary {
  incomingStock: UniqueArticle[]
  currentStock: UniqueArticle[]
  deliveredArticles: UniqueArticle[]
  lostArticles: UniqueArticle[]
}

// Per-product aggregation row used by store inventory stats and dashboards.
export interface ProductWithCount extends Product {
  count: number
  ca: number
}

// Per-inventory counts already classified into the same buckets as the global
// aggregation. Lets the BO render a per-inventory row inline without
// re-fetching the inventory and re-classifying its articles.
export interface InventoryCounts {
  currentStock: number
  incomingStock: number
  delivered: number
  sold: number
  destroyed: number
  defective: number
  stolen: number
  errored: number
}

// Lightweight inventory header used in the aggregated stats payload (no preloads).
export interface InventoryHeader {
  id: number
  label: string
  createdAt: string
  validatedAt: string | null
  isFinished: boolean
  status: InventoryStatus
  counts: InventoryCounts
}

// Per-bucket result. `totalCa` is only meaningful on sold/destroyed/defective/stolen
// (used in the dashboard percentages); delivered/errored leave it omitted.
export interface InventoryAggregateBucket {
  products: ProductWithCount[]
  totalCount: number
  totalCa: number
}

export interface InventoriesAggregatedStats {
  inventories: InventoryHeader[]
  buckets: {
    sold: InventoryAggregateBucket
    delivered: InventoryAggregateBucket
    destroyed: InventoryAggregateBucket
    defective: InventoryAggregateBucket
    stolen: InventoryAggregateBucket
    errored: InventoryAggregateBucket
  }
}

export interface MinStockValues extends TimestampsAndID {
  weekEndMinimumStock?: number
  mondayMinimumStock?: number
  tuesdayMinimumStock?: number
  wednesdayMinimumStock?: number
  thursdayMinimumStock?: number
  fridayMinimumStock?: number
}

export const DAYS_OF_WEEK = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const

export type DayOfWeek = (typeof DAYS_OF_WEEK)[number]

export const frenchDaysOfWeek: Record<DayOfWeek, string> = {
  monday: 'Lundi',
  tuesday: 'Mardi',
  wednesday: 'Mercredi',
  thursday: 'Jeudi',
  friday: 'Vendredi',
  saturday: 'Samedi',
  sunday: 'Dimanche',
}

export interface DailyMinsAndSold {
  [key: string]: {
    minMap: { [key: string]: number | undefined }

    soldMap: { [key: string]: { totalCount: number; totalDays: number; history: number[] } }
  }
}

export interface Seller extends TimestampsAndID {
  reference?: string
  label: string
  address?: Address
  establishments?: Establishment[]
}

export interface Establishment extends TimestampsAndID {
  reference?: string
  label: string
  address?: Address
  seller?: Seller
}

export const OPENING_MODE_VALUES = [
  'UNATTENDED',
  'ATTENDED',
  'CLOSED',
  'UNATTENDED_AUTHENTICATED',
  'ATTENDED_PRE_AUTHORIZATION',
  'FREE_ENTRY',
] as const
export type OpeningMode = (typeof OPENING_MODE_VALUES)[number]

export const frenchOpeningMode: Record<OpeningMode, string> = {
  UNATTENDED: 'Ouverture classique',
  ATTENDED: 'Ouverture manuelle',
  CLOSED: 'Fermeture complète',
  UNATTENDED_AUTHENTICATED: 'Ouverture avec compte Lucie',
  ATTENDED_PRE_AUTHORIZATION: 'Ouverture avec pré-autorisation',
  FREE_ENTRY: 'Toujours ouvert',
}

export interface OpeningConfigLine {
  openingMode: OpeningMode
  beginTime: string
  endTime: string
  preAuthorizationAmount?: number
}

export interface OpeningConfigLineString {
  openingMode: OpeningMode
  beginTime: string
  endTime: string
  preAuthorizationAmount?: number
}

export interface OpeningConfigDBLine {
  openingMode: OpeningMode
  beginTime: string // hh:mm
  preAuthorizationAmount?: number
}

export type OpeningConfigDB = Record<number, OpeningConfigDBLine[]>

export interface OpeningConfigValidationResponse {
  status: 'success' | 'error'
  message: string
}

export interface PendingStoreKiosk {
  kioskId: number
  storeId: number
  storeLabel?: string
}

export interface VisioLink {
  url: string
  message: string
  token: string
}

export interface VisioReview extends TimestampsAndID {
  visioRoomId: number
  transcript: string
  grade: number | null
  noGradeReason: NoGradeReason | null
  gradeExplanation: string | null
  durationSeconds: number | null
  managerGrade: number | null
  managerComment: string | null
  managerId: number | null
  managerReviewedAt: string | null
  aiGradeValidatedBy: number | null
  aiGradeValidatedAt: string | null
}

// -- Visio Reviews Dashboard Types -- //

export interface VisioReviewInteraction {
  id: number
  visioRoomId: number
  grade: number | null
  gradeExplanation: string | null
  noGradeReason: NoGradeReason | null
  managerGrade: number | null
  managerComment: string | null
  managerId: number | null
  managerReviewedAt: string | null
  durationSeconds: number | null
  resolutionTimestampSeconds: number | null
  aiGradeValidatedBy: number | null
  aiGradeValidatedAt: string | null
  resolutionStatus: ResolutionStatus | null
  interactionAt: string
  operator: { id: number; firstName: string; lastName: string }
  store: { id: number; label: string }
  kiosk: { id: number; type: string }
}

export type VisioReviewInteractionsSortBy = 'time' | 'rating' | 'duration'
export type VisioReviewSortDir = 'asc' | 'desc'

export interface VisioReviewInteractionsResponse {
  rows: VisioReviewInteraction[]
  hasMorePage: boolean
  /** Only present on page 1 (i.e. when the request omits `page` or sets it to 1). */
  totalCount?: number
}

/**
 * `no-previous-data` / `no-current-data` ⇒ one of the windows has no rated
 * visios; trend is not computable. Companion `trendValue` is null in those
 * states.
 */
export type VisioReviewTrend = 'up' | 'down' | 'stable' | 'no-previous-data' | 'no-current-data'

export interface VisioReviewOperatorStats {
  adminId: number
  firstName: string
  lastName: string
  avgGrade: number | null
  visioCount: number
  avgDurationSeconds: number | null
  trend: VisioReviewTrend
  trendValue: number | null
}

export type VisioReviewOperatorsSortBy = 'rating' | 'visios' | 'duration'

export type VisioReviewGradeBucket =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '10'
  | 'Sans note'

export interface VisioReviewOperatorRanked {
  adminId: number
  firstName: string
  lastName: string
}

export interface VisioReviewOperatorsCharts {
  byAvgGrade: (VisioReviewOperatorRanked & { avgGrade: number })[]
  topByVolume: (VisioReviewOperatorRanked & { visioCount: number })[]
}

export interface VisioReviewOperatorsResponse {
  rows: VisioReviewOperatorStats[]
  hasMorePage: boolean
  /** Only present on page 1 (i.e. when the request omits `page` or sets it to 1). */
  totalCount?: number
}

export interface VisioReviewOperatorsChartsResponse {
  charts: VisioReviewOperatorsCharts
}

export interface VisioReviewOperatorDetail {
  kpis: {
    avgGrade: number | null
    totalVisios: number
    avgDurationSeconds: number | null
    /** 0–1, or null when no row in the window had a `resolution_status`. */
    resolutionRate: number | null
  }
  charts: {
    ratingDistribution: { range: VisioReviewGradeBucket; count: number }[]
    /** Empty when the date range is unbounded (Toujours). */
    trendChart: { label: string; avgGrade: number }[]
  }
}

export interface VisioReviewOperatorInteractionsResponse {
  rows: VisioReviewInteraction[]
  hasMorePage: boolean
}

export interface VisioReviewStoreStats {
  storeId: number
  storeName: string
  /** Total visios in the current window — graded or not. */
  interactionCount: number
  // TODO(review #6): missedCalls metric removed pending domain definition
  /** Null when no visios in the window were graded. */
  avgGrade: number | null
  avgDurationSeconds: number | null
  trend: VisioReviewTrend
  trendValue: number | null
}

export type VisioReviewStoresSortBy = 'rating' | 'interactions' | 'name'

export interface VisioReviewStoresResponse {
  rows: VisioReviewStoreStats[]
  hasMorePage: boolean
  /** Only present on page 1 (i.e. when the request omits `page` or sets it to 1). */
  totalCount?: number
}

export interface VisioReviewStoresCharts {
  byAvgGrade: { storeId: number; storeName: string; avgGrade: number }[]
  byVolume: { storeId: number; storeName: string; interactionCount: number }[]
}

export interface VisioReviewStoresChartsResponse {
  charts: VisioReviewStoresCharts
}

export interface VisioReviewStoreDetail {
  kpis: {
    avgGrade: number | null
    totalVisios: number
    avgDurationSeconds: number | null
    /** 0–1, or null when no row in the window had a `resolution_status`. */
    resolutionRate: number | null
  }
  charts: {
    ratingDistribution: { range: VisioReviewGradeBucket; count: number }[]
    /** Empty when the date range is unbounded (Toujours). */
    trendChart: { label: string; avgGrade: number }[]
  }
}

export interface VisioReviewStoreOperator {
  adminId: number
  firstName: string
  lastName: string
  visioCount: number
  avgGrade: number | null
}

export interface VisioReviewStoreOperatorsResponse {
  rows: VisioReviewStoreOperator[]
  hasMorePage: boolean
}

export interface VisioReviewStoreInteractionsResponse {
  rows: VisioReviewInteraction[]
  hasMorePage: boolean
}

export interface VisioReviewManagerEvaluation {
  managerGrade: number
  managerComment?: string
}

export interface VisioReviewConfig {
  problemPrompt: string | null
  openPrompt: string | null
  lowGradeThreshold: number | null
  highGradeThreshold: number | null
}

export interface VisioReviewFilterOptions {
  stores: { id: number; label: string }[]
  operators: { id: number; firstName: string; lastName: string }[]
}

export interface VisioReviewRecordingUrlResponse {
  url: string
  mimeType: string
  expiresInSeconds: number
}

export interface VisioReviewTranscriptResponse {
  transcript: string | null
}

// -- Global tab charts -- //

export interface VisioReviewRatingBands {
  red: number
  amber: number
  green: number
  noGrade: number
}

export interface VisioReviewFunnel {
  totalVisios: number
  gradedVisios: number
  validatedVisios: number
  resolvedVisios: number
}

export interface VisioReviewTrendPoint {
  bucketStart: string
  avgGrade: number | null
  prevAvgGrade: number | null
}

export interface VisioReviewVolumeTimelinePoint {
  bucketStart: string
  count: number
  red: number
  amber: number
  green: number
  noGrade: number
}

export type VisioReviewBucketing = 'day' | 'week' | 'month'

export interface VisioReviewGlobalCharts {
  ratingBands: {
    current: VisioReviewRatingBands
    previous: VisioReviewRatingBands | null
  }
  funnel: VisioReviewFunnel
  trend: VisioReviewTrendPoint[]
  volumeTimeline: VisioReviewVolumeTimelinePoint[]
  ratingDistribution: { range: VisioReviewGradeBucket; count: number }[]
  bucketing: VisioReviewBucketing
}

export interface VisioReviewGlobalChartsResponse {
  charts: VisioReviewGlobalCharts
}

// -- Activity heatmap (per operator / per store, used in detail dialogs) -- //

export interface VisioReviewActivityHeatmapCell {
  /** 0 = Monday, …, 6 = Sunday (Europe/Paris). */
  dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6
  /** 0..23 (Europe/Paris). */
  hour: number
  count: number
}

export interface VisioReviewActivityHeatmap {
  cells: VisioReviewActivityHeatmapCell[]
  max: number
}

export interface StoreProductPrice extends TimestampsAndID {
  productReference: string
  storeReference: string
  sellPrice: number | null
  franco: number | null
  coldStartStockMin: number | null
  agPromo: number | null
  product?: Product
  store?: Store
  agPrice?: number | null
}

export interface StoreCategoryMinimumStockCompute extends TimestampsAndID {
  storeId: number
  internalCategoryId: number
  growthRate: number
  breakageRate: number
  lookbackWeeks: 1 | 2 | 3 | 4
}

export const FRIDGE_TYPES = {
  FRIDGE: 'FRIDGE',
  COLD_ROOM: 'COLD_ROOM',
} as const
export type FridgeType = (typeof FRIDGE_TYPES)[keyof typeof FRIDGE_TYPES]

export const CONTACT_TYPES = ['EMAIL', 'PHONE', 'WEBSITE', 'ADDRESS', 'SOCIAL'] as const
export type ContactType = (typeof CONTACT_TYPES)[number]

export interface JvcCamera {
  id: number
  label: string
}

export interface CameraStream extends TimestampsAndID {
  storeId: number
  domain: string
  username: string
  password: string
  cameras: JvcCamera[]
}

export interface StoreCameras {
  type: CameraType | null
  cameras: JvcCamera[]
}

export interface SetStoreCameraTypeInput {
  storeId: number
  type: CameraType | null
}

export interface UpdateCameraStreamInput {
  storeId: number
  domain?: string
  username?: string
  password?: string
  cameras?: JvcCamera[]
}

export interface StoreCameraConfigSummary extends TimestampsAndID {
  storeId: number
  hasVpnConfig: boolean
  hasVpnCredentials: boolean
  runtime: StoreCameraRuntimeSummary | null
}

export interface StoreCameraRuntimeSummary {
  status: string
  hostname: string | null
  baseUrl: string | null
  instanceId: string | null
  instanceState: string | null
  publicIp: string | null
  privateIp: string | null
  lastReconcileRequestedAt: string | null
  lastReconcileStartedAt: string | null
  lastReconcileSucceededAt: string | null
  lastReconcileFailedAt: string | null
  lastErrorMessage: string | null
}

export interface StoreCameraConfigState {
  config: StoreCameraConfigSummary | null
  runtime: StoreCameraRuntimeSummary | null
}

export interface StoreCameraConfigUpsertInput {
  storeId: number
  vpnConfig?: string
  vpnCredentials?: string
}

export interface StoreCamera extends TimestampsAndID {
  storeId: number
  label: string
  name: string
  streamUrl: string
}

export interface StoreCameraViewSession {
  baseUrl: string
  token: string
  expiresAt: string
  runtimeStatus: string
}

export interface Go2RtcStreamProducer {
  url: string
}

export interface Go2RtcStream {
  producers?: Go2RtcStreamProducer[]
  consumers?: unknown[]
}

export type Go2RtcStreamMap = Record<string, Go2RtcStream>

export interface CreateStoreCameraInput {
  storeId: number
  label: string
  streamUrl: string
}

export interface UpdateStoreCameraInput {
  storeId: number
  cameraId: number
  label?: string
  streamUrl?: string
}

// ********************************************************* //
//                    REAL ESTATE                            //
// ********************************************************* //

export const STORE_PROJECT_STATUSES = [
  'BEFORE_PROJECT',
  'UNDER_STUDY',
  'ADVANCED_PROJECT',
  'SECURED',
  'WORKS',
  'OPENABLE',
  'OPEN',
] as const
export type StoreProjectStatus = (typeof STORE_PROJECT_STATUSES)[number]

export const TASK_STATUSES = ['TODO', 'IN_PROGRESS', 'DONE'] as const
export type TaskStatus = (typeof TASK_STATUSES)[number]

export const INSEE_DEMOGRAPHICS_YEARS = [2022] as const
export type InseeDemographicsYear = (typeof INSEE_DEMOGRAPHICS_YEARS)[number]

export const INSEE_DEMOGRAPHICS_SOURCES = ['INSEE'] as const
export type InseeDemographicsSource = (typeof INSEE_DEMOGRAPHICS_SOURCES)[number]

export interface InseeDemographics {
  inhabitants: number
  workers15p: number
}

export interface InseeFullDemographics {
  year: InseeDemographicsYear
  source: InseeDemographicsSource
  demographics: Record<number, InseeDemographics>
}

// Demographics zone types for store project analysis
export const DEMOGRAPHICS_ZONES = ['r1', 'r2', 'r3', 'r4'] as const
export type DemographicsZone = (typeof DEMOGRAPHICS_ZONES)[number]

export const DEMOGRAPHICS_RADIUS_MAP: Record<DemographicsZone, number> = {
  r1: 400, // 5 min walking
  r2: 800, // 10 min walking
  r3: 2000, // 5 min driving
  r4: 4000, // 10 min driving
}

export const DEMOGRAPHICS_ZONE_LABELS: Record<DemographicsZone, string> = {
  r1: '5 min à pied (~ 400m)',
  r2: '10 min à pied (~ 800m)',
  r3: '5 min en voiture (~ 2km)',
  r4: '10 min en voiture (~ 4km)',
}

// Store project contact
export interface StoreProjectContact {
  id: string
  name: string
  email?: string
  phone?: string
  contactRole?: ContactRole | null
}

// Store project document
export interface StoreProjectDocument {
  url: string
  name: string
  size: number
  uploadedAt: string
}

// Polymorphic person reference (can be either an admin or a contact)
export interface PersonReference {
  adminId?: number | null
  contactId?: number | null
}

// ---- Sub-table form data types ----

export interface StoreProjectWorksFormData {
  workDurationWeeks: number | null
  keyReceptionDate: string | null
  workStartDate: string | null
  workEndDate: string | null
  openingDate: string | null
  moeExecution: PersonReference
  projectManagerAdminId: number | null
  fitter: PersonReference
  preliminaryVisitDate: string | null
  technicalAuditTodoDate: string | null
  technicalAuditPlannedDate: string | null
  technicalAuditDoneDate: string | null
  proDceTodoDate: string | null
  proDceStartedDate: string | null
  proDceDoneDate: string | null
  proDceTargetDate: string | null
  validatedGoBudget: number | null
  dgdBudget: number | null
}

export interface StoreProjectLeaseFormData {
  projectTypology: ProjectTypology | null
  implementationTypology: ImplementationTypology | null
  contractType: LeaseContractType | null
  totalFloorArea: number | null
  salesFloorArea: number | null
  backOfficeFloorArea: number | null
  glaSurface: number | null
  expectedSigningDate: string | null
  loiValidationDate: string | null
  promiseProjectedDate: string | null
  promiseSignatureDate: string | null
  signatureDate: string | null
  securisation: SecurisationType | null
  leaseStartDate: string | null
  coproApproval: CoproApprovalStatus | null
}

export interface StoreProjectDesignFormData {
  designRequestDate: string | null
  designStatus: DesignStatus | null
  fridgeCountX: number | null
  fridgeCountY: number | null
  fridgeCountZ: number | null
  checkoutCount: number | null
  checkinVersion: CheckinVersion | null
  validatedPlanUrl: string | null
  screen24Gourmandise: number | null
  screen26Gourmandise: number | null
  screen24Cafe: number | null
  screen26Cafe: number | null
  screen26Fridges: number | null
  externalScreens: number | null
}

export interface StoreProjectPermitsFormData {
  designMoe: PersonReference
  dpFilingDate: string | null
  dpObtainedDate: string | null
  atFilingDate: string | null
  atObtainedDate: string | null
  deFilingDate: string | null
  deObtainedDate: string | null
  launchClearance: LaunchClearanceStatus | null
}

// Store project form data type (for form management)
export interface StoreProjectFormData {
  name: string
  devId: string | null
  dev: number | null
  address: string
  city: string
  postalCode: string
  surfaceArea: number | null
  score: number | null
  status: StoreProjectStatus
  isAbandoned: boolean
  rentPerYear: number | null
  works: number | null
  leaseFunds: number | null
  otherCosts: string | null
  storeName: string | null
  contacts: StoreProjectContact[]
  googleMapsUrl: string | null
  latitude: number | null
  longitude: number | null
  photoUrls: string[]
  documents: StoreProjectDocument[]
  comment: string
  worksData: StoreProjectWorksFormData | null
  leaseData: StoreProjectLeaseFormData | null
  designData: StoreProjectDesignFormData | null
  permitsData: StoreProjectPermitsFormData | null
}

export interface RealEstateKpis {
  avgLandCostEur: number | null
  avgLandCostPercentage: number | null
  avgWorksBudgetEur: number | null
  avgWorksBudgetPercentage: number | null
  openStoresDelta30d: number | null
  averageAnnualCaEur: number | null
}

export interface NetworkDashboardDailyAggregate {
  date: string
  totalCa: number
  totalCaHt: number
  totalBuyingPrice: number
  totalProductsSold: number
  totalCartsCount: number
  totalStolenCount: number
  totalStolenBuyingValue: number
  totalDestroyedCount: number
  totalDestroyedBuyingValue: number
  storeCount: number
  matureStoreCount: number
  matureCa: number
  matureCaHt: number
  matureBuyingPrice: number
  matureProductsSold: number
  matureCartsCount: number
  matureStolenCount: number
  matureStolenBuyingValue: number
  matureDestroyedCount: number
  matureDestroyedBuyingValue: number
}

export interface NetworkDashboardStoreWeeklyCa {
  storeId: number
  storeLabel: string
  currentWeekCa: number
  previousWeekCa: number
}

export interface NetworkDashboardCategoryPdm {
  categoryId: number
  name: string
  caSharePercent: number
  uvcSharePercent: number
}

export interface NetworkDashboardTopProduct {
  reference: string
  label: string
  uvc: number
  previousUvc: number
  categoryIds: number[]
}

export interface NetworkDashboardHrMetrics {
  avgETP: number
  avgSalaryCostPerStore: number
  inPersonHoursTotal: number
  remoteHoursTotal: number
}

// Store-age filter for the network dashboard.
//  - `default`: keep each card's historical behaviour (CA total uses all
//    stores, other cards use mature stores only).
//  - `all`: every card aggregates over every store.
//  - `mature`: every card aggregates over mature stores only.
export const NETWORK_DASHBOARD_STORE_AGE_FILTERS = ['default', 'all', 'mature'] as const

export type NetworkDashboardStoreAgeFilter = (typeof NETWORK_DASHBOARD_STORE_AGE_FILTERS)[number]

export const DEFAULT_NETWORK_DASHBOARD_STORE_AGE_FILTER: NetworkDashboardStoreAgeFilter = 'default'

export interface NetworkDashboardAvailableStore {
  id: number
  label: string
  departmentCode: string | null
}

export interface NetworkDashboardKpis {
  dailyAggregates: NetworkDashboardDailyAggregate[]
  storeWeeklyCas: NetworkDashboardStoreWeeklyCa[]
  categoryPdm: NetworkDashboardCategoryPdm[]
  topProducts: NetworkDashboardTopProduct[]
  hrMetrics: NetworkDashboardHrMetrics
  availableStores: NetworkDashboardAvailableStore[]
}

export interface StoreCaOverDays {
  caEur: number | null
}

export interface NearbyPlace {
  displayNameText: string
  primaryType: string | undefined
  formattedAddress?: string | undefined
  lat: number
  lng: number
  userRating?: number
  userRatingsTotal?: number
  websiteUri?: string
  inseeMatchScore?: number
  caYear?: number
  caAmount?: number
}

export interface AppAccordionItem<TLabel = unknown, TContent = unknown> {
  id: string
  label: TLabel
  content: TContent
}

export const GOOGLE_MAPS_PLACE_TYPES = [
  'bakery',
  'meal_delivery',
  'restaurant',
  'sandwich_shop',
  'butcher_shop',
  'convenience_store',
  'food_store',
  'grocery_store',
  'market',
  'supermarket',
] as const
export type GoogleMapsPlaceType = (typeof GOOGLE_MAPS_PLACE_TYPES)[number]

export const ACCEPTED_DOCUMENT_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.jpg',
  '.jpeg',
  '.png',
  '.txt',
] as const
export type AcceptedDocumentExtension = (typeof ACCEPTED_DOCUMENT_EXTENSIONS)[number]

// ********************************************************* //
//                    LEGAL ENTITIES                         //
// ********************************************************* //

export interface LegalEntity extends TimestampsAndID {
  name: string
  description: string
  siret: string
}

// ********************************************************* //
//                         HR                                 //
// ********************************************************* //

// Collaborator data - represents an employee/collaborator
export interface Collaborator extends TimestampsAndID {
  // Admin account used to manage the collaborator in the BO
  adminId: number
  // Matricule of the collaborator
  matricule: string
  // Role of the collaborator (e.g., 'adv', 'responsable-magasin')
  role: CollaboratorRole
  // Contract type (e.g., 'cdi', 'cdd', 'interim')
  contractType: ContractType
  // Status of the collaborator (e.g., 'actif', 'inactif')
  status: CollaboratorStatus
  // Phone number
  phone: string
  // Optional manager, stored as another collaborator id
  managerId: number | null
  // Weekly working minutes
  weeklyWorkingMinutes: number
  // Hourly cost in cents
  hourlyCost: number
  // S3 key for the collaborator profile picture
  profilePictureKey: string | null
  // Public URL computed from profilePictureKey
  profilePictureUrl: string | null
  // Computed (backend): id of the store of the active main store period.
  // Requires `activeMainStorePeriod` to be preloaded.
  mainStoreId?: number | null
  // Computed (backend): store of the active main store period.
  // Requires `activeMainStorePeriod` to be preloaded with its `store`.
  mainStore?: Store | null
  // Computed (backend): true if a formation period is currently active.
  // Requires `activeFormation` to be preloaded.
  isInFormation?: boolean
  // Optional relations (not always preloaded)
  admin?: Admin
  manager?: Collaborator
  stores?: Store[]
  workTimes?: CollaboratorWorkTime[]
  unavailabilities?: CollaboratorUnavailability[]
  equipment?: CollaboratorEquipment[]
  clothing?: CollaboratorClothing[]
  workShifts?: WorkShift[]
  // History of all main store assignments (most recent first)
  mainStorePeriods?: CollaboratorMainStore[]
  // Currently active main store period; null if none
  activeMainStorePeriod?: CollaboratorMainStore | null
  // History of all formation periods (most recent first)
  formationPeriods?: CollaboratorFormation[]
  // Currently active formation period; null if none
  activeFormation?: CollaboratorFormation | null
}

// Collaborator work time - weekly work ranges for planning
export interface CollaboratorWorkTime extends TimestampsAndID {
  collaboratorId: number
  // Day of the week (e.g., 'monday', 'tuesday')
  dayOfWeek: WeekDay
  // Start time stored as "HH:mm"
  startTime: string
  // End time stored as "HH:mm"
  endTime: string
  // Optional relation (not always preloaded)
  collaborator?: Collaborator
}

// Collaborator unavailability - absences and unavailability periods
export interface CollaboratorUnavailability extends TimestampsAndID {
  collaboratorId: number
  // Reason for the absence
  reason: UnavailabityReason
  // Date range for the absence (ISO date string)
  startDate: string
  endDate: string
  // Optional time range when the absence is partial (stored as "HH:mm")
  startTime: string | null
  endTime: string | null
  // Optional relation (not always preloaded)
  collaborator?: Collaborator
}

// Flattened collaborator unavailability for weekly calendar display
export interface CollaboratorUnavailabilityCalendarItem {
  unavailabilityId: number
  collaboratorId: number
  collaboratorFullName: string
  reason: UnavailabityReason
  startDate: string
  endDate: string
  startTime: string | null
  endTime: string | null
}

// Collaborator equipment - assigned equipment items
export interface CollaboratorEquipment extends TimestampsAndID {
  collaboratorId: number
  // Type of equipment (e.g., 'computer', 'tablet', 'phone')
  type: EquipmentType
  // Date when the equipment was given (ISO date string)
  givenDate: string
  // Serial number of the equipment
  serialNumber: string
  // Brand of the equipment
  brand: string
  // Optional relation (not always preloaded)
  collaborator?: Collaborator
}

// Collaborator clothing - assigned clothing items
export interface CollaboratorClothing extends TimestampsAndID {
  collaboratorId: number
  // Type of clothing (e.g., 't-shirt', 'shirt')
  type: ClothingType
  // Size of the clothing (e.g., 'XS', 'S', 'M', 'L', 'XL', 'XXL')
  size: ClothingSize
  // Quantity of items
  quantity: number
  // Optional relation (not always preloaded)
  collaborator?: Collaborator
}

// Collaborator main store period - successive main store assignments.
// `endedAt === null` => active period (at most one per collaborator,
// enforced by a partial unique index in DB).
// During an active formation period, costs of all shifts are imputed to
// the store of the active main store period at the shift's date.
export interface CollaboratorMainStore extends TimestampsAndID {
  collaboratorId: number
  storeId: number
  // ISO datetime when the period started
  startedAt: string
  // ISO datetime when the period ended; null if still active
  endedAt: string | null
  // Audit: admin who opened / closed the period
  startedById: number | null
  endedById: number | null
  // Optional relations (not always preloaded)
  collaborator?: Collaborator
  store?: Store
  startedBy?: Admin | null
  endedBy?: Admin | null
}

// Collaborator formation period - manager-toggled "in formation" flag.
// `endedAt === null` => formation in progress (at most one active per
// collaborator, enforced by a partial unique index in DB).
// Start / end are exclusively driven by manager actions, never automated.
export interface CollaboratorFormation extends TimestampsAndID {
  collaboratorId: number
  // ISO datetime when the formation started
  startedAt: string
  // ISO datetime when the formation ended; null if still in progress
  endedAt: string | null
  // Audit: admin who started / ended the formation
  startedById: number | null
  endedById: number | null
  // Optional relations (not always preloaded)
  collaborator?: Collaborator
  startedBy?: Admin | null
  endedBy?: Admin | null
}

// Work shift - scheduled work shifts for store scheduling
export interface WorkShiftClock extends TimestampsAndID {
  workShiftId: number | null
  collaboratorId: number
  loggedById: number
  action: WorkShiftClockAction
  clockedAt: string
  temoinFileKey: string
  temoinFileUrl?: string
  // Optional relations (not always preloaded)
  workShift?: WorkShift
  admin?: Admin
}

export interface AttendanceHistoryItem extends WorkShiftClock {
  collaborator?: Collaborator
}

export interface WorkShift extends TimestampsAndID {
  collaboratorId: number
  storeId: number
  // Planned schedule (ISO date string)
  date: string
  // Start time stored as "HH:mm"
  startTime: string
  endTime: string
  validatedAt?: string | null
  validatedById?: number | null
  validatedBy?: Admin | null
  validationStatus?: 'pending' | 'validated'
  actualStartTime?: string | null
  actualEndTime?: string | null
  // Overnight shift linking: child shift points to its parent
  parentShiftId?: number | null
  // Convenience id of the child shift (present on parent of an overnight shift)
  childShiftId?: number | null
  // Optional child shift (present on parent of an overnight shift)
  childShift?: WorkShift | null
  // Optional parent shift (present on child of an overnight shift)
  parentShift?: WorkShift | null
  // Optional clock events
  clockValues?: WorkShiftClock[]
  isInFormation?: boolean
  // Optional relations (not always preloaded)
  collaborator?: Collaborator
  store?: Store
}

export interface HrAnalyticsResponse {
  period: {
    dateStart: string
    dateEnd: string
  }
  headcount: {
    total: number
    onSite: number
    remote: number
  }
  workedHours: {
    averagePerWeek: number
    averagePerDay: Record<WeekDay, number>
  }
  salaryCost: {
    // Total salary cost in integer cents.
    amount: number
    histogram: {
      date: string
      // Salary cost bucket in integer cents.
      amount: number
    }[]
  }
}

export type HrSalesPeaksResponse = Record<
  DayOfWeek,
  {
    time: string
    cartsSold: number
    cartsPct: number
  }[]
>

export interface HrStoreWeeklyMetricsResponse {
  period: {
    timezone: 'Europe/Paris'
    startDate: string
    endDate: string
    excludesToday: boolean
  }
  store: {
    storeId: number
    workedHours: {
      amount: number
      // Salary cost in integer cents.
      salaryCost: number
    }
    remoteHours: number
    // Salary cost rate in cents per uncovered remote hour.
    remoteHourlySalaryCost: number
    // Revenue in integer cents.
    revenue: { caTtc: number }
    formationHoursCovered: number
    formationCostsImputed: number
    formationImputedHours: number
    formationCoveredCollabs: {
      collaboratorId: number
      firstName: string
      lastName: string
      profilePictureUrl: string | null
      mainStoreName: string
      hours: number
    }[]
    formationImputedCollabs: {
      collaboratorId: number
      firstName: string
      lastName: string
      profilePictureUrl: string | null
      executionStoreName: string
      hours: number
      cost: number
    }[]
  }
  global: {
    workedHours: {
      amount: number
      // Salary cost in integer cents.
      salaryCost: number
    }
    // Revenue in integer cents.
    revenue: { caTtc: number }
  }
  comparisons: {
    // Percentage change vs same store's previous week (S-1). null when no previous data.
    previousWeek: {
      presentielCostPct: number | null
      distancielCostPct: number | null
      totalStaffCostPct: number | null
    }
    // Percentage diff vs average of OTHER boutiques with >= 1 shift this week. null when no other boutique.
    network: {
      presentielCostPct: number | null
      distancielCostPct: number | null
      totalStaffCostPct: number | null
    }
    // Staff cost / previous complete week revenue ratio.
    staffCostRevenueRatio: {
      storeRatio: number | null
      networkRatio: number | null
    }
  }
}

export interface HrRemoteStorePeriodMetricsResponse {
  period: {
    timezone: 'Europe/Paris'
    startDate: string
    endDate: string
    excludesToday: boolean
  }
  storeId: number
  // Heures effectuées physiquement sur ce store remote par des collabs en
  // formation rattachés ailleurs. Déjà incluses dans `day.hours + night.hours` ;
  // exposées séparément pour la traçabilité (le coût correspondant a été
  // imputé à la mainStore du collaborateur, pas à ce store remote).
  formationHoursCovered: number
  day: {
    hours: number
    // Salary cost in integer cents.
    salaryCost: number
  }
  night: {
    hours: number
    // Salary cost in integer cents.
    salaryCost: number
  }
}

export interface PublishPlanningSmsResponse {
  status: 'ok' | 'partial'
  period: {
    dateStart: string
    dateEnd: string
    timezone: 'Europe/Paris'
  }
  storeId: number
  collaboratorsWithShifts: number
  smsSent: number
  failed: number
  message: string
}

export interface PublicPlanningChildShift {
  id: number
  date: string // YYYY-MM-DD
  startTime: string // HH:mm:ss
  endTime: string
  actualStartTime: string | null
  actualEndTime: string | null
}

export interface PublicPlanningShift {
  id: number
  collaboratorId: number
  date: string
  startTime: string
  endTime: string
  actualStartTime: string | null
  actualEndTime: string | null
  validationStatus: 'pending' | 'validated'
  parentShiftId: number | null
  childShift: PublicPlanningChildShift | null
}

export interface PublicPlanningResponse {
  collaborator: {
    id: number
    firstName: string | null
    lastName: string | null
  }
  collaborators: { id: number; firstName: string | null; lastName: string | null }[]
  store: {
    id: number
    label: string
    address: {
      street: string
      postalCode: string
      city: string
    } | null
  }
  period: {
    weekStart: string // YYYY-MM-DD
    weekEnd: string
    timezone: 'Europe/Paris'
  }
  shifts: PublicPlanningShift[]
}

export interface CopyPlanningWeekData {
  storeId: number
  sourceWeekNumber: number
  sourceYear: number
  targetWeekNumber: number
  targetYear: number
}

export interface CopyPlanningWeekPeriod {
  weekNumber: number
  year: number
  dateStart: string
  dateEnd: string
}

export interface CopyPlanningWeekSkippedShift {
  sourceWorkShiftId: number
  collaboratorId: number
  collaboratorName: string
  targetDate: string
  startTime: string
  endTime: string
  reason: string
}

export interface CopyPlanningWeekResponse {
  status: 'ok' | 'partial'
  storeId: number
  sourcePeriod: CopyPlanningWeekPeriod
  targetPeriod: CopyPlanningWeekPeriod
  totalSourceShifts: number
  created: number
  skipped: number
  skippedShifts: CopyPlanningWeekSkippedShift[]
  message: string
}

// ********************************************************* //
//                  PLANNING CONFIG                          //
// ********************************************************* //

export interface PlanningGlobalConfig extends TimestampsAndID {
  maxWeeklyHours: number
  maxDailyHours: number
  maxShiftDurationHours: number
  maxConsecutiveWorkDays: number
  minWeeklyRestHours: number
  breakTriggerHours: number
  breakDurationMinutes: number
  maxDailyBreaks: number
}

export interface PlanningStoreStaffingRule extends TimestampsAndID {
  storeId: number
  minTurnover: number
  maxTurnover: number
  staffCount: number
}

export interface PlanningStoreConfig extends TimestampsAndID {
  storeId: number
  lunchBreakMinutes: number
  staffingRules?: PlanningStoreStaffingRule[]
}

export interface PlanningConfig {
  global: PlanningGlobalConfig
  storeConfigs: PlanningStoreConfig[]
}

export interface ConstraintCheckItem {
  collaboratorId: number
  collaboratorName: string
  value: number
  limit: number
  respected: boolean
  detail: string
}

export interface ConstraintsReport {
  maxWeeklyHours: ConstraintCheckItem[]
  maxShiftDuration: ConstraintCheckItem[]
  maxConsecutiveWorkDays: ConstraintCheckItem[]
  minBreakBetweenShifts: ConstraintCheckItem[]
}

export interface PlanningLastOptimization {
  id: number
  status: PlanningOptimizationStatus
  startedAt: string
  finishedAt: string | null
  createdShifts: number | null
  updatedShifts: number | null
  adminFirstName: string
  adminLastName: string
}

export type OptimizePlanningSkippedKind = 'create' | 'extend' | 'overnight-child'

export interface OptimizePlanningSkippedShift {
  kind: OptimizePlanningSkippedKind
  collaboratorId: number
  collaboratorName: string
  storeId: number
  date: string
  startTime: string
  endTime: string
  reason: string
}

// ********************************************************* //
//                    XLSX IMPORT                            //
// ********************************************************* //
export interface RowError {
  reference: string
  message: string
}

// When at least one row is valid, this is the response
export interface XlsxImportResponse {
  message: string
  errors: RowError[]
  updated: number
  created: number
  failed: number
}

export interface ParsingRowError {
  row: number
  message: string
}

// When there is a global xlsx parsing error, this is the response
export interface XlsxImportError {
  message: string
  errors: ParsingRowError[]
}

export * from './store-project-fields'

// ********************************************************* //
//                       STREAM                              //
// ********************************************************* //

export * from './stream/stream'

// ********************************************************* //
//                       ENUMS                               //
// ********************************************************* //

export * from './enums/index'

// ********************************************************* //
//                       CONSTANTS                           //
// ********************************************************* //

export * from './constants'
export * from './rbac'
export * from './schemas/dispatchSimulation'
export * from './schemas/platformStocks'
export * from './schemas/reapproSimulation'

// ********************************************************* //
//                  REFERENCED BRANDS                        //
// ********************************************************* //

export interface ReferencedBrandCategory extends TimestampsAndID {
  name: string
  shortName: string | null
  color: string
  nafCodes: string[]
  brandsCount?: number
}

export interface ReferencedBrand extends TimestampsAndID {
  categoryId: number
  name: string
  namePattern: string
  searchQuery: string | null
  inseeEnseigne: string | null
  active: boolean
  lastScrapedAt: string | null
  category?: ReferencedBrandCategory
  poisCount?: number
}

export interface ReferencedBrandPoi {
  id: number
  brandId: number
  lat: number
  lng: number
  name: string | null
  address: string | null
}

export interface ReferencedBrandsStats {
  categoriesCount: number
  brandsCount: number
  poisCount: number
}

export interface ReferencedBrandScrapeResponse {
  status: 'queued' | 'busy'
  brandsCount: number
  message: string
}

// ********************************************************* //
//                  INTEREST ZONE RUNS                       //
// ********************************************************* //

export interface InterestZoneFilter {
  categoryId: number | null
  minCount: number
}

export interface InterestZoneRunParams {
  maxRadiusKm: number
  minPopulation: number
  filters: InterestZoneFilter[]
}

export interface InterestZoneRunStats {
  seedsEligible: number
  zonesBuilt: number
  zonesDiscarded: number
  totalIrisUsed: number
  totalPopulation: number
  pop: { min: number; max: number; mean: number; median: number }
  radiusMeters: { min: number; max: number; mean: number; median: number }
}

export type InterestZoneRunStatus = 'pending' | 'completed' | 'failed'

export interface InterestZoneRun {
  id: number
  key: string
  status: InterestZoneRunStatus
  params: InterestZoneRunParams
  stats: InterestZoneRunStats | null
  fileKey: string | null
  error: string | null
  createdAt: string
  completedAt: string | null
}

export interface InterestZoneRunWithUrl extends InterestZoneRun {
  fileUrl: string | null
}

export interface InterestZoneFeatureProperties {
  zoneId: number
  seedIrisCode: string
  irisCount: number
  population: number
  radiusMeters: number
  centroidLat: number
  centroidLng: number
  poisByCategoryId: Record<string, number>
  poisTotal: number
  maxPopIrisCode: string
  maxPopIrisName: string
  maxPopIrisPop: number
  maxPopIrisGeometry: GeoJSON.Polygon | GeoJSON.MultiPolygon
}

export type InterestZoneFeatureCollection = GeoJSON.FeatureCollection<
  GeoJSON.Polygon | GeoJSON.MultiPolygon,
  InterestZoneFeatureProperties
>

// ********************************************************* //
//                     PLATFORM STOCKS                       //
// ********************************************************* //

export interface PlatformStockLog extends TimestampsAndID {
  platformStockId: number | null
  adminId: number | null
  storeReference: string | null
  quantityBefore: number
  quantityAfter: number
  source: string
  comment: string | null
  deletedAt: string | null
  revertedAt: string | null
  revertedById: number | null
  deletedById: number | null
  admin?: Admin | null
  store?: Store | null
}

export interface PlatformStock extends TimestampsAndID {
  productReference: string
  storeReference: string
  lot: string
  quantity: number
  product?: Product | null
  store?: Store | null
  logs?: PlatformStockLog[]
}

export interface PaginatedPlatformStocks {
  data: PlatformStock[]
  meta: {
    total: number
    perPage: number
    currentPage: number
    lastPage: number
  }
}

export interface PlatformStockImportResult {
  updated: number
  errors: { reference: string; message: string }[]
}

export interface PlatformStockProductOption {
  reference: string
  label: string
}
